import { createClient } from '@supabase/supabase-js'

import { isSupabaseConfigured, supabase } from './supabase'
import { formatSupabaseAuthError } from './supabase-auth-errors'
import type { DoctorAccessGrant, DoctorAccessGrantInput } from '@/types/health'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || ''
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || ''
const LOCAL_DOCTOR_ACCESS_KEY = 'medtrack-local-doctor-access'

export interface DoctorAccessCreationInput {
  patientId: string
  patientDisplayName: string
  doctorName: string
  doctorEmail: string
  doctorSpecialty?: string | null
}

export interface DoctorAccessCredentials {
  loginIdentifier: string
  doctorEmail: string
  accessCode: string
  loginUrl: string
}

export interface DoctorAccessLinkHint {
  patientId: string
  patientDisplayName: string
  doctorName: string
  doctorEmail: string
  doctorSpecialty?: string | null
  loginIdentifier: string
}

function readLocalDoctorAccesses(): DoctorAccessGrant[] {
  if (typeof window === 'undefined') {
    return []
  }

  const stored = window.localStorage.getItem(LOCAL_DOCTOR_ACCESS_KEY)

  if (!stored) {
    return []
  }

  try {
    return JSON.parse(stored) as DoctorAccessGrant[]
  } catch {
    return []
  }
}

function writeLocalDoctorAccesses(items: DoctorAccessGrant[]) {
  if (typeof window === 'undefined') {
    return
  }

  window.localStorage.setItem(LOCAL_DOCTOR_ACCESS_KEY, JSON.stringify(items))
}

function upsertLocalDoctorAccess(grant: DoctorAccessGrant) {
  const currentItems = readLocalDoctorAccesses()
  const deduped = currentItems.filter(
    (item) =>
      !(
        normalizeEmail(item.doctor_email) === normalizeEmail(grant.doctor_email) &&
        item.patient_id === grant.patient_id
      )
  )

  writeLocalDoctorAccesses([grant, ...deduped].sort((left, right) => right.created_at.localeCompare(left.created_at)))
}

function revokeMatchingLocalDoctorAccess(access: {
  id?: string | null
  patientId?: string | null
  doctorEmail?: string | null
}, revokedAt: string) {
  const normalizedEmail = normalizeEmail(access.doctorEmail || '')

  const nextItems = readLocalDoctorAccesses().map((item) => {
    const matchesById = access.id ? item.id === access.id : false
    const matchesByPatientEmail =
      Boolean(access.patientId) &&
      normalizedEmail.length > 0 &&
      item.patient_id === access.patientId &&
      normalizeEmail(item.doctor_email) === normalizedEmail

    if (!matchesById && !matchesByPatientEmail) {
      return item
    }

    return {
      ...item,
      is_active: false,
      revoked_at: revokedAt,
      updated_at: revokedAt
    }
  })

  writeLocalDoctorAccesses(nextItems)
}

function buildLocalDoctorAccessGrant(args: {
  patientId: string
  patientDisplayName: string
  doctorId: string
  doctorName: string
  doctorEmail: string
  doctorSpecialty?: string | null
  loginIdentifier: string
  accessCodePreview: string
  createdAt?: string
}): DoctorAccessGrant {
  const createdAt = args.createdAt || nowIso()

  return {
    id: crypto.randomUUID(),
    patient_id: args.patientId,
    patient_display_name: args.patientDisplayName,
    doctor_id: args.doctorId,
    doctor_name: args.doctorName.trim(),
    doctor_email: normalizeEmail(args.doctorEmail),
    doctor_specialty: args.doctorSpecialty?.trim() || null,
    login_identifier: args.loginIdentifier,
    access_code_preview: args.accessCodePreview,
    is_active: true,
    revoked_at: null,
    created_at: createdAt,
    updated_at: createdAt
  }
}

function mergeDoctorAccesses(primary: DoctorAccessGrant[], secondary: DoctorAccessGrant[]) {
  const merged = new Map<string, DoctorAccessGrant>()

  for (const item of [...primary, ...secondary]) {
    const key = `${item.patient_id}::${normalizeEmail(item.doctor_email)}`
    const existing = merged.get(key)

    if (!existing || item.updated_at > existing.updated_at) {
      merged.set(key, item)
    }
  }

  return [...merged.values()].sort((left, right) => right.created_at.localeCompare(left.created_at))
}

function createIsolatedSupabaseClient() {
  return createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      storageKey: `doctor-access-${Math.random().toString(36).slice(2)}`
    }
  })
}

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase()
}

export function normalizeDoctorAccessCode(value: string): string {
  const upperCased = value
    .trim()
    .toUpperCase()
    .replace(/[‐‑‒–—―﹘﹣－_]/g, '-')
    .replace(/\s+/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')

  const compact = upperCased.replace(/-/g, '')

  if (/^[A-Z0-9]{12}$/.test(compact)) {
    return `${compact.slice(0, 4)}-${compact.slice(4, 8)}-${compact.slice(8, 12)}`
  }

  return upperCased
}

function buildAccessCode(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  const segments = [4, 4, 4].map((length) =>
    Array.from({ length }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join('')
  )

  return segments.join('-')
}

function nowIso(): string {
  return new Date().toISOString()
}

async function ensureDoctorProfile(
  authClient: ReturnType<typeof createIsolatedSupabaseClient>,
  doctorId: string,
  doctorEmail: string,
  doctorName: string
) {
  try {
    const { error } = await authClient
      .from('profiles')
      .upsert({
        id: doctorId,
        email: doctorEmail,
        full_name: doctorName.trim(),
        role: 'doctor'
      })

    if (error) {
      console.warn('Impossible de synchroniser le profil medecin:', error.message)
    }
  } catch (error) {
    console.warn('Synchronisation du profil medecin ignoree:', error)
  }
}

async function verifyDoctorCredentials(
  authClient: ReturnType<typeof createIsolatedSupabaseClient>,
  doctorEmail: string,
  accessCode: string
) {
  const { error } = await authClient.auth.signInWithPassword({
    email: doctorEmail,
    password: accessCode
  })

  if (!error) {
    await authClient.auth.signOut()
    return
  }

  if (/invalid login credentials/i.test(error.message)) {
    throw new Error(
      'Cet email medecin existe deja avec un ancien mot de passe. Utilisez une autre adresse email ou faites reinitialiser ce compte.'
    )
  }

  throw new Error(formatSupabaseAuthError(error, { operation: 'doctor-signin' }))
}

export function buildDoctorAccessUrl(
  loginIdentifier: string,
  hint?: Partial<DoctorAccessLinkHint>
): string {
  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'https://health.doux-labs.com'
  const searchParams = new URLSearchParams()
  searchParams.set('id', loginIdentifier)

  if (hint?.patientId) {
    searchParams.set('patient', hint.patientId)
  }

  if (hint?.patientDisplayName) {
    searchParams.set('patientName', hint.patientDisplayName)
  }

  if (hint?.doctorName) {
    searchParams.set('doctorName', hint.doctorName)
  }

  if (hint?.doctorEmail) {
    searchParams.set('doctorEmail', normalizeEmail(hint.doctorEmail))
  }

  if (hint?.doctorSpecialty) {
    searchParams.set('specialty', hint.doctorSpecialty)
  }

  return `${origin}/doctor-access?${searchParams.toString()}`
}

export function registerDoctorAccessHintFromLink(hint: DoctorAccessLinkHint) {
  const localGrant = buildLocalDoctorAccessGrant({
    patientId: hint.patientId,
    patientDisplayName: hint.patientDisplayName,
    doctorId: `link:${normalizeEmail(hint.doctorEmail)}`,
    doctorName: hint.doctorName,
    doctorEmail: hint.doctorEmail,
    doctorSpecialty: hint.doctorSpecialty,
    loginIdentifier: hint.loginIdentifier,
    accessCodePreview: 'link'
  })

  upsertLocalDoctorAccess(localGrant)
}

export async function listDoctorAccessesForPatient(
  patientId: string
): Promise<DoctorAccessGrant[]> {
  const localItems = readLocalDoctorAccesses().filter((item) => item.patient_id === patientId)

  if (isSupabaseConfigured) {
    const { data, error } = await supabase
      .from('doctor_patient_access')
      .select('*')
      .eq('patient_id', patientId)
      .order('created_at', { ascending: false })

    if (!error) {
      return mergeDoctorAccesses((data || []) as DoctorAccessGrant[], localItems)
    }

    console.warn('Lecture des acces medecin via Supabase impossible:', error.message)
  }

  return localItems.sort((left, right) => right.created_at.localeCompare(left.created_at))
}

export async function listDoctorAccessesForDoctor(
  doctorId: string
): Promise<DoctorAccessGrant[]> {
  const {
    data: { user }
  } = await supabase.auth.getUser()
  const doctorEmail = normalizeEmail(user?.email || '')
  const localItems = readLocalDoctorAccesses().filter(
    (item) =>
      item.is_active &&
      !item.revoked_at &&
      (item.doctor_id === doctorId ||
        normalizeEmail(item.doctor_email) === doctorEmail ||
        normalizeEmail(item.login_identifier) === doctorEmail)
  )

  if (isSupabaseConfigured) {
    const { data, error } = await supabase
      .from('doctor_patient_access')
      .select('*')
      .eq('doctor_id', doctorId)
      .eq('is_active', true)
      .is('revoked_at', null)
      .order('created_at', { ascending: false })

    if (!error) {
      return mergeDoctorAccesses((data || []) as DoctorAccessGrant[], localItems)
    }

    console.warn('Lecture des acces patient du medecin impossible:', error.message)
  }

  return localItems.sort((left, right) => right.created_at.localeCompare(left.created_at))
}

export async function createDoctorAccess(
  input: DoctorAccessCreationInput
): Promise<{ grant: DoctorAccessGrant; credentials: DoctorAccessCredentials }> {
  const doctorEmail = normalizeEmail(input.doctorEmail)
  const accessCode = normalizeDoctorAccessCode(buildAccessCode())
  const loginIdentifier = doctorEmail
  const createdAt = nowIso()

  if (isSupabaseConfigured) {
    const authClient = createIsolatedSupabaseClient()

    try {
      const { data: signUpData, error: signUpError } = await authClient.auth.signUp({
        email: doctorEmail,
        password: accessCode,
        options: {
          data: {
            full_name: input.doctorName.trim(),
            role: 'doctor'
          }
        }
      })

      if (signUpError) {
        if (/already registered/i.test(signUpError.message)) {
          throw new Error(
            'Un compte medecin existe deja pour cet identifiant. Utilisez un autre email ou revoquez puis recreez l acces.'
          )
        }

        throw new Error(formatSupabaseAuthError(signUpError, { operation: 'doctor-create' }))
      }

      if (!signUpData.user?.id) {
        throw new Error('Creation du compte medecin impossible')
      }

      await ensureDoctorProfile(
        authClient,
        signUpData.user.id,
        doctorEmail,
        input.doctorName
      )

      await verifyDoctorCredentials(authClient, doctorEmail, accessCode)

      const payload: DoctorAccessGrantInput = {
        patient_id: input.patientId,
        patient_display_name: input.patientDisplayName,
        doctor_id: signUpData.user.id,
        doctor_name: input.doctorName.trim(),
        doctor_email: doctorEmail,
        doctor_specialty: input.doctorSpecialty?.trim() || null,
        login_identifier: loginIdentifier,
        access_code_preview: accessCode.slice(-4),
        is_active: true,
        revoked_at: null
      }

      const { data: grantData, error: grantError } = await supabase
        .from('doctor_patient_access')
        .upsert(payload, { onConflict: 'doctor_id,patient_id' })
        .select('*')
        .single()

      if (grantError) {
        console.warn('Creation Supabase du lien medecin refusee, fallback local active:', grantError.message)

        const fallbackGrant = buildLocalDoctorAccessGrant({
          patientId: input.patientId,
          patientDisplayName: input.patientDisplayName,
          doctorId: signUpData.user.id,
          doctorName: input.doctorName,
          doctorEmail,
          doctorSpecialty: input.doctorSpecialty,
          loginIdentifier,
          accessCodePreview: accessCode.slice(-4),
          createdAt
        })

        upsertLocalDoctorAccess(fallbackGrant)

        return {
          grant: fallbackGrant,
          credentials: {
            loginIdentifier,
            doctorEmail,
            accessCode,
            loginUrl: buildDoctorAccessUrl(loginIdentifier, {
              patientId: input.patientId,
              patientDisplayName: input.patientDisplayName,
              doctorName: input.doctorName,
              doctorEmail,
              doctorSpecialty: input.doctorSpecialty
            })
          }
        }
      }

      return {
        grant: grantData as DoctorAccessGrant,
        credentials: {
          loginIdentifier,
          doctorEmail,
          accessCode,
          loginUrl: buildDoctorAccessUrl(loginIdentifier, {
            patientId: input.patientId,
            patientDisplayName: input.patientDisplayName,
            doctorName: input.doctorName,
            doctorEmail,
            doctorSpecialty: input.doctorSpecialty
          })
        }
      }
    } finally {
      void authClient.auth.signOut()
    }
  }

  const grant = buildLocalDoctorAccessGrant({
    patientId: input.patientId,
    patientDisplayName: input.patientDisplayName,
    doctorId: crypto.randomUUID(),
    doctorName: input.doctorName,
    doctorEmail,
    doctorSpecialty: input.doctorSpecialty,
    loginIdentifier,
    accessCodePreview: accessCode.slice(-4),
    createdAt
  })
  upsertLocalDoctorAccess(grant)

  return {
    grant,
    credentials: {
      loginIdentifier,
      doctorEmail,
      accessCode,
      loginUrl: buildDoctorAccessUrl(loginIdentifier, {
        patientId: input.patientId,
        patientDisplayName: input.patientDisplayName,
        doctorName: input.doctorName,
        doctorEmail,
        doctorSpecialty: input.doctorSpecialty
      })
    }
  }
}

export async function revokeDoctorAccess(access: Pick<DoctorAccessGrant, 'id' | 'patient_id' | 'doctor_email'>): Promise<void> {
  const revokedAt = nowIso()
  let supabaseRevoked = false

  if (isSupabaseConfigured) {
    const { data, error } = await supabase
      .from('doctor_patient_access')
      .update({
        is_active: false,
        revoked_at: revokedAt,
        updated_at: revokedAt
      })
      .eq('id', access.id)
      .select('id')

    if (!error && (data || []).length > 0) {
      supabaseRevoked = true
    } else {
      const { data: fallbackData, error: fallbackError } = await supabase
        .from('doctor_patient_access')
        .update({
          is_active: false,
          revoked_at: revokedAt,
          updated_at: revokedAt
        })
        .eq('patient_id', access.patient_id)
        .eq('doctor_email', normalizeEmail(access.doctor_email))
        .select('id')

      if (!fallbackError && (fallbackData || []).length > 0) {
        supabaseRevoked = true
      } else if (error || fallbackError) {
        console.warn(
          'Revocation de l acces medecin via Supabase impossible:',
          fallbackError?.message || error?.message
        )
      }
    }
  }

  revokeMatchingLocalDoctorAccess(
    {
      id: access.id,
      patientId: access.patient_id,
      doctorEmail: access.doctor_email
    },
    revokedAt
  )

  if (!supabaseRevoked && !isSupabaseConfigured) {
    return
  }
}
